// External packages
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Hooks
import { useToast } from 'hooks/useToast';
import { API_BASE_URL, authHeaders } from './base';

export interface CreateLicense {
  uuid: string;
  validUntil: string;
}

export interface LicenseResponse {
  error: boolean;
  data: License[];
}

export interface Customer {
  id: number;
  uuid: string;
  name: string | null;
  email: string | null;
  createdAt: string;
  updatedAt: string;
  metadata: any;
}

export interface License {
  id: number;
  code: string;
  activationMethod: string;
  printed: boolean;
  validUntil: string | null;
  redeemedAt: string | null;
  customerId: number | null;
  customer: Customer | null;
}

export async function getLicenses(): Promise<License[]> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/license`,
      { headers: authHeaders() }
    );

    if (!response.ok) throw new Error('Failed to fetch Licenses');

    const result: LicenseResponse = await response.json();

    if (result.error) throw new Error('Failed to fetch Licenses');

    return result.data;
  } catch (error: any) {
    throw new Error(error.message || 'Network error during fetching Licenses');
  }
}

export const useGetLicenses = () => {
  return useQuery({
    queryKey: ['Licenses'],
    queryFn: getLicenses,
    initialData: [],
  });
};

export const createLicense = async (
  data: Partial<License>,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/license`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(data),
      }
    );
    if (!response.ok)
      throw new Error(`Failed to create License: ${response.statusText}`);
    return response.json();
  } catch (error: any) {
    throw new Error(error.message || 'Network error during License creation');
  }
};

export const useCreateLicense = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ data }: { data: Partial<License> }) => createLicense(data),
    onSuccess: () => {
      queryClient?.invalidateQueries?.({ queryKey: ['Licenses'] });
      toast.success('Licenca je uspješno kreirana!');
    },
    onError: (error: any) => {
      console.error('Failed to create license:', error);
      toast.error(error?.message || 'Greška pri kreiranju licence.');
    },
  });
};

export interface RedeemLicensePayload {
  code: string;
  uuid: string;
  activationMethod: string;
}

export const redeemLicense = async (
  payload: RedeemLicensePayload,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/license/redeem`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok)
      throw new Error(`Failed to redeem License: ${response.statusText}`);
    return response.json();
  } catch (error: any) {
    throw new Error(error.message || 'Network error during License redeem');
  }
};

export const useRedeemLicense = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: RedeemLicensePayload) => redeemLicense(payload),
    onSuccess: () => {
      queryClient?.invalidateQueries?.({ queryKey: ['Licenses'] });
      toast.success('Licenca je uspješno iskorištena!');
    },
    onError: (error: any) => {
      console.error('Failed to redeem license:', error);
      toast.error(error?.message || 'Greška pri iskorištavanju licence.');
    },
  });
};

export const updateLicensePrinted = async (
  id: number,
  printed: boolean,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/license/${id}/printed`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ printed }),
    });

    if (!response.ok)
      throw new Error(`Failed to update license: ${response.statusText}`);

    return response.json();
  } catch (error: any) {
    throw new Error(
      error.message || 'Network error during updating license printed status'
    );
  }
};

export const useUpdateLicensePrinted = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, printed }: { id: number; printed: boolean }) =>
      updateLicensePrinted(id, printed),
    onSuccess: () => {
      queryClient?.invalidateQueries?.({ queryKey: ['Licenses'] });
    },
    onError: (error: any) => {
      console.error('Failed to update license printed status:', error);
      toast.error(
        error?.message || 'Greška pri promjeni statusa ispisane licence.'
      );
    },
  });
};
